import React, { useState, useEffect } from 'react';
import { Download, Users, TrendingUp, Loader2, Award, FileText } from 'lucide-react';
import api from '../../api';

const MarksSummary = ({ 
  assignmentId, 
  classId, 
  subjectId, 
  academicYearId,
  componentId,
  componentName,
  onBack
}) => {
  const [aggregatedData, setAggregatedData] = useState(null);
  const [loadingAggregation, setLoadingAggregation] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [cieMaxMarks, setCieMaxMarks] = useState(40);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [availableComponents, setAvailableComponents] = useState([]);

  // Wrapped in its own useCallback to establish structural reference identity
  const fetchAggregatedDataWithParams = React.useCallback(async (cid, sid, ayid) => {
    setLoadingAggregation(true);
    try {
      const response = await api.get(`/api/aggregation/class/${cid}/subject/${sid}`, {
        params: { academicYearId: ayid }
      });
      
      if (response.data.success) {
        const data = response.data.data;
        setAggregatedData(data);
        
        // Extract available components from first student
        if (data.students && data.students.length > 0) {
          const firstStudent = data.students[0];
          if (firstStudent.componentScores && firstStudent.componentScores.length > 0) {
            const components = firstStudent.componentScores.map(c => ({
              id: c.componentId,
              name: c.componentName,
              contribution: c.contributionToCIE,
              maxPossible: c.maxPossibleContribution || 0,
              weightage: c.weightageInCIE
            }));
            setAvailableComponents(components);
            
            // Auto-select the component that matches the passed componentName or first one
            let defaultComponent = null;
            if (componentName) {
              defaultComponent = components.find(c => 
                c.name?.toLowerCase() === componentName?.toLowerCase()
              );
            }
            if (!defaultComponent && componentId) {
              defaultComponent = components.find(c => c.id === componentId);
            }
            if (!defaultComponent && components.length > 0) {
              defaultComponent = components[0];
            }
            setSelectedComponent(defaultComponent);
            
            // Calculate total CIE max
            const totalPossible = components.reduce((sum, c) => sum + (c.maxPossible || 0), 0);
            if (totalPossible > 0) {
              setCieMaxMarks(totalPossible);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching aggregated data:', error);
      setAggregatedData(null);
    } finally {
      setLoadingAggregation(false);
    }
  }, [componentName, componentId]);

  // Safely references stable fetchAggregatedDataWithParams dependency
  const fetchAggregatedData = React.useCallback(async () => {
    if (!classId || !subjectId) {
      setLoadingAggregation(false);
      return;
    }
    await fetchAggregatedDataWithParams(classId, subjectId, academicYearId);
  }, [classId, subjectId, academicYearId, fetchAggregatedDataWithParams]);

  // Safely references stable fetchAggregatedDataWithParams dependency
  const fetchContextFromAssignment = React.useCallback(async () => {
    try {
      const response = await api.get(`/api/faculty/paper-template-assignment/${assignmentId}`);
      if (response.data.success && response.data.assignment) {
        const assignment = response.data.assignment;
        await fetchAggregatedDataWithParams(assignment.classId, assignment.subjectId, assignment.academicYearId);
      }
    } catch (error) {
      console.error('Error fetching assignment context:', error);
      setLoadingAggregation(false);
    }
  }, [assignmentId, fetchAggregatedDataWithParams]);

  useEffect(() => {
    if (classId && subjectId && academicYearId) {
      fetchAggregatedData();
    } else if (assignmentId) {
      fetchContextFromAssignment();
    }
  }, [classId, subjectId, academicYearId, assignmentId, fetchAggregatedData, fetchContextFromAssignment]);

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast(t => ({ ...t, show: false })), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  const showNotification = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const handleComponentSelect = (component) => {
    setSelectedComponent(component);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      if (aggregatedData && aggregatedData.students && selectedComponent) {
        const exportData = aggregatedData.students.map(student => {
          const component = student.componentScores?.find(c => c.componentId === selectedComponent.id);
          const contribution = component?.contributionToCIE || 0;
          
          return {
            'USN': student.studentUSN,
            'Student Name': student.studentName,
            [`${selectedComponent.name} Contribution to CIE`]: `${contribution} / ${cieMaxMarks}`,
            'Percentage of Component': component?.componentPercentage?.toFixed(1) || '0',
            'Raw Score': component?.rawScore ? `${component.rawScore}/${component.maxRawScore}` : 'N/A'
          };
        });
        
        const csvHeaders = Object.keys(exportData[0] || {}).join(',');
        const csvRows = exportData.map(row => Object.values(row).join(','));
        const csv = [csvHeaders, ...csvRows].join('\n');
        
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${selectedComponent.name}_cie_contribution.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        showNotification('Export completed successfully!', 'success');
      } else {
        showNotification('No data available to export', 'error');
      }
    } catch (error) {
      console.error('Export error:', error);
      showNotification('Failed to export data', 'error');
    } finally {
      setExporting(false);
    }
  };

  if (loadingAggregation) {
    return (
      <div className="w-full bg-white rounded-2xl border border-gray-100 p-12 text-center">
        <Loader2 className="w-7 h-7 text-blue-600 animate-spin mx-auto mb-3" />
        <p className="text-gray-500 font-medium text-xs">Loading component data...</p>
      </div>
    );
  }

  if (!aggregatedData || !aggregatedData.students || aggregatedData.students.length === 0) {
    return (
      <div className="w-full bg-white rounded-2xl border border-gray-100 p-12 text-center">
        <Award className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <h3 className="text-sm font-bold text-gray-700">No Data Available</h3>
        <p className="text-xs text-gray-400 mt-1">No marks have been entered yet.</p>
      </div>
    );
  }

  const students = aggregatedData.students || [];

  // Calculate stats for selected component
  let totalContribution = 0;
  let studentsWithData = 0;
  
  if (selectedComponent) {
    students.forEach(student => {
      const component = student.componentScores?.find(c => c.componentId === selectedComponent.id);
      const contribution = component?.contributionToCIE || 0;
      totalContribution += contribution;
      if (contribution > 0) studentsWithData++;
    });
  }
  
  const averageContribution = studentsWithData > 0 ? (totalContribution / studentsWithData).toFixed(1) : 0;

  return (
    <div className="space-y-4 font-sans antialiased">
      
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center px-4 py-3 rounded-xl shadow-xl text-white font-semibold text-xs transition-all duration-300 ${
          toast.type === 'error' ? 'bg-rose-600' : 'bg-emerald-600'
        }`}>
          <span className="mr-2 text-sm">{toast.type === 'error' ? '⚠️' : '✨'}</span>
          {toast.message}
        </div>
      )}

      {/* Component Selector Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <h2 className="font-black text-gray-900">
                {selectedComponent?.name || 'Component'} - CIE Contribution
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Marks contributed to total CIE (Max: {cieMaxMarks} marks)
              </p>
            </div>
          </div>
          
          {/* Component Switcher - Show all available components */}
          {availableComponents.length > 1 && (
            <div className="flex gap-2">
              {availableComponents.map(comp => (
                <button
                  key={comp.id}
                  onClick={() => handleComponentSelect(comp)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    selectedComponent?.id === comp.id
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {comp.name} ({comp.weightage}%)
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase">Total Students</p>
              <p className="text-2xl font-black text-gray-900">{students.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-emerald-50 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase">Average Contribution</p>
              <p className="text-2xl font-black text-emerald-600">{averageContribution}</p>
              <p className="text-[9px] text-gray-400">out of {cieMaxMarks} CIE marks</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-purple-50 rounded-lg flex items-center justify-center">
              <Award className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase">Students with Data</p>
              <p className="text-2xl font-black text-purple-600">{studentsWithData}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Component Marks Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        
        <div className="p-4 bg-slate-50/50 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-800">
              {selectedComponent?.name || 'Component'} CIE Marks
            </h3>
            <p className="text-[10px] text-gray-400 mt-0.5">
              Contribution to total CIE (Max: {cieMaxMarks} marks)
            </p>
          </div>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-medium disabled:opacity-40 transition-all"
          >
            {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            Export
          </button>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full border-collapse">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">USN</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Student Name</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase">
                  {selectedComponent?.name || 'Component'} Contribution
                </th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase">Component %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {students.map((student, idx) => {
                const component = student.componentScores?.find(c => c.componentId === selectedComponent?.id);
                const contribution = component?.contributionToCIE || 0;
                const componentPercentage = component?.componentPercentage || 0;
                
                return (
                  <tr key={idx} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs font-bold text-blue-600">
                      {student.studentUSN}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-800">
                      {student.studentName}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-black text-emerald-600 text-lg">
                        {contribution}
                      </span>
                      <span className="text-gray-400 text-xs"> / {cieMaxMarks}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`font-semibold text-sm ${componentPercentage >= 70 ? 'text-emerald-600' : componentPercentage >= 50 ? 'text-amber-600' : 'text-rose-600'}`}>
                        {componentPercentage.toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile View */}
        <div className="block md:hidden divide-y divide-gray-100">
          {students.map((student, idx) => {
            const component = student.componentScores?.find(c => c.componentId === selectedComponent?.id);
            const contribution = component?.contributionToCIE || 0;
            
            return (
              <div key={idx} className="p-4 flex justify-between items-center">
                <div>
                  <p className="font-mono text-[10px] text-gray-400">{student.studentUSN}</p>
                  <p className="font-bold text-gray-900 text-sm">{student.studentName}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400">CIE Contribution</p>
                  <p className="font-black text-emerald-600 text-lg">{contribution}</p>
                  <p className="text-[9px] text-gray-400">/ {cieMaxMarks}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-3 bg-gray-50 border-t border-gray-200 text-center">
          <p className="text-[9px] text-gray-400">
            {selectedComponent?.name || 'Component'} contribution to total CIE (Max: {cieMaxMarks} marks)
          </p>
        </div>
      </div>
    </div>
  );
};

export default MarksSummary;